import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, SERVICES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PhotoUpload from "../components/PhotoUpload";
import { ArrowLeft, ArrowRight, CheckCircle, Leaf, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const STEPS = ["Contact", "Service", "Photos", "Review"];

export default function QuoteForm() {
  const nav = useNavigate();
  const topRef = useRef(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    frequency: "",
    frequency_cadence: "",
    services: [],
    other_service: "",
    waste_disposal: "",
    visit_preference: "",
    notes: "",
  });
  const [photos, setPhotos] = useState([]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id) ? f.services.filter((s) => s !== id) : [...f.services, id],
    }));
  };

  const validate = () => {
    if (step === 0) {
      if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.address.trim()) {
        toast.error("Please fill in your name, phone, email and address."); return false;
      }
      if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error("Please enter a valid email."); return false; }
    }
    if (step === 1) {
      if (!form.frequency) { toast.error("Please choose a service frequency."); return false; }
      if (form.frequency === "regular" && !form.frequency_cadence) { toast.error("Please choose Weekly or Fortnightly."); return false; }
      if (form.services.length === 0) { toast.error("Please choose at least one service."); return false; }
      if (form.services.includes("other") && !form.other_service.trim()) { toast.error("Please describe the 'Other' service."); return false; }
      if (!form.waste_disposal) { toast.error("Please choose a garden waste disposal option."); return false; }
      if (!form.visit_preference) { toast.error("Please choose your visit preference."); return false; }
    }
    return true;
  };

  const next = () => { if (validate()) setStep((s) => Math.min(STEPS.length - 1, s + 1)); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  // Scroll to top of form whenever the step changes — and remove any lingering keyboard focus
  useEffect(() => {
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step, submittedId]);

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const frequency = form.frequency === "one_off" ? "one_off" : form.frequency_cadence;
    const payload = {
      name: form.name,
      phone: form.phone,
      email: form.email.toLowerCase().trim(),
      address: form.address,
      frequency,
      services: form.services,
      other_service: form.other_service,
      waste_disposal: form.waste_disposal,
      visit_preference: form.visit_preference,
      notes: form.notes,
      photo_ids: photos.map((p) => p.id),
    };
    try {
      const { data } = await axios.post(`${API}/quotes`, payload);
      setSubmittedId(data.id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ")
        : (typeof detail === "string" ? detail : "Couldn't submit your request. Please try again.");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedId) {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16 hs-fade" data-testid="quote-success-screen">
        <div className="rounded-2xl border border-[#E5E0D8] bg-white p-8 sm:p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#E3EBDA] text-[#3C5A3F]">
            <CheckCircle size={32} weight="fill" />
          </div>
          <h1 className="mt-5 font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight text-[#2C3E35]">
            Thank you!
          </h1>
          <p className="mt-3 text-[#73777B]">
            We are reviewing your photos and will send your quote shortly. Your quote will arrive from{" "}
            <a href="mailto:Admin@honeystone.uk" className="font-semibold text-[#4F6F52] underline decoration-[#86A789] underline-offset-2">Admin@honeystone.uk</a>
            {" "}to <span className="font-semibold text-[#2C3E35]">{form.email}</span> — please add us to your contacts so it doesn't end up in spam.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => nav(`/status?email=${encodeURIComponent(form.email)}`)} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6" data-testid="success-view-status">
              View my quote status
            </Button>
            <Button variant="outline" onClick={() => nav("/")} className="rounded-full h-11 px-6" data-testid="success-back-home">
              Back to home
            </Button>
          </div>
          <div className="mt-8 pt-6 border-t border-[#E5E0D8]" data-testid="success-direct-contact">
            <p className="text-sm text-[#73777B]">Prefer to talk? Get in touch direct:</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-center">
              <a href="tel:+447384361344" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6 text-sm font-semibold transition-colors" data-testid="success-call-link">
                <Phone size={16} weight="fill" /> Call 07384 361344
              </a>
              <a href={"https://wa.me/447384361344?text=" + encodeURIComponent("Hi Honeystone, I'd like a quote for my garden/home.")} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1FB857] text-white h-11 px-6 text-sm font-semibold transition-colors" data-testid="success-whatsapp-link">
                <WhatsappLogo size={16} weight="fill" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 pb-[50vh] sm:pb-32 hs-fade" data-testid="quote-form">
      <div ref={topRef} aria-hidden="true" />
      <div className="mb-8">
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight text-[#2C3E35]">Tell us about the job</h1>
        <p className="mt-2 text-[#73777B]">Get a no-obligation quote in under 2 minutes.</p>
      </div>

      {/* Progress */}
      <div className="mb-8 flex items-center gap-2" data-testid="quote-form-progress">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`flex-1 h-1.5 rounded-full ${i <= step ? "bg-[#4F6F52]" : "bg-[#E5E0D8]"}`} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs font-medium text-[#73777B] mb-6">
        <span>Step {step + 1} of {STEPS.length}</span>
        <span>{STEPS[step]}</span>
      </div>

      <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        {step === 0 && (
          <div className="space-y-5" data-testid="step-contact">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Jane Smith" data-testid="input-name" className="mt-1.5" autoComplete="name" enterKeyHint="next" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone number</Label>
                <Input id="phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="07700 900000" data-testid="input-phone" className="mt-1.5" autoComplete="tel" enterKeyHint="next" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" inputMode="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@example.com" data-testid="input-email" className="mt-1.5" autoComplete="email" autoCapitalize="off" enterKeyHint="next" />
              </div>
            </div>
            <div>
              <Label htmlFor="address">Postcode / Address</Label>
              <Input id="address" value={form.address} onChange={(e) => setField("address", e.target.value)} placeholder="e.g. GL56 0AA or full address" data-testid="input-address" className="mt-1.5" autoComplete="street-address" autoCapitalize="characters" enterKeyHint="done" />
              <p className="text-xs text-[#73777B] mt-1.5" data-testid="postcode-reassurance">
                Used only to confirm you are in our local service area.
              </p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-7" data-testid="step-service">
            <div>
              <Label className="text-base">Service frequency</Label>
              <RadioGroup value={form.frequency} onValueChange={(v) => setField("frequency", v)} className="mt-3 grid gap-3">
                <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer ${form.frequency === "one_off" ? "border-[#4F6F52] bg-[#F4F8F1]" : "border-[#E5E0D8]"}`}>
                  <RadioGroupItem value="one_off" id="freq-one" data-testid="freq-one-off" />
                  <span className="text-sm font-medium text-[#2C3E35]">One-off job</span>
                </label>
                <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer ${form.frequency === "regular" ? "border-[#4F6F52] bg-[#F4F8F1]" : "border-[#E5E0D8]"}`}>
                  <RadioGroupItem value="regular" id="freq-regular" data-testid="freq-regular" />
                  <span className="text-sm font-medium text-[#2C3E35]">Regular ongoing maintenance</span>
                </label>
              </RadioGroup>
              {form.frequency === "regular" && (
                <div className="mt-4">
                  <Label htmlFor="cadence" className="text-sm">How often?</Label>
                  <Select value={form.frequency_cadence} onValueChange={(v) => setField("frequency_cadence", v)}>
                    <SelectTrigger id="cadence" className="mt-1.5" data-testid="freq-cadence-trigger">
                      <SelectValue placeholder="Choose cadence" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly" data-testid="freq-weekly">Weekly</SelectItem>
                      <SelectItem value="fortnightly" data-testid="freq-fortnightly">Fortnightly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <Label className="text-base">Services needed</Label>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {SERVICES.map((s) => (
                  <label key={s.id} className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${form.services.includes(s.id) ? "border-[#4F6F52] bg-[#F4F8F1]" : "border-[#E5E0D8]"}`}>
                    <Checkbox checked={form.services.includes(s.id)} onCheckedChange={() => toggleService(s.id)} data-testid={`service-${s.id}`} />
                    <span className="text-sm text-[#2C3E35]">{s.label}</span>
                  </label>
                ))}
              </div>
              {form.services.includes("other") && (
                <div className="mt-3">
                  <Label htmlFor="other">Please describe</Label>
                  <Input id="other" value={form.other_service} onChange={(e) => setField("other_service", e.target.value)} placeholder="What else can we help with?" data-testid="input-other-service" className="mt-1.5" />
                </div>
              )}
            </div>

            <div>
              <Label className="text-base">Garden waste disposal</Label>
              <RadioGroup value={form.waste_disposal} onValueChange={(v) => setField("waste_disposal", v)} className="mt-3 grid gap-3">
                <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer ${form.waste_disposal === "green_bin" ? "border-[#4F6F52] bg-[#F4F8F1]" : "border-[#E5E0D8]"}`}>
                  <RadioGroupItem value="green_bin" id="w-bin" data-testid="waste-green-bin" />
                  <span className="text-sm text-[#2C3E35]">I have a green council bin (leave waste on site)</span>
                </label>
                <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer ${form.waste_disposal === "remove_dispose" ? "border-[#4F6F52] bg-[#F4F8F1]" : "border-[#E5E0D8]"}`}>
                  <RadioGroupItem value="remove_dispose" id="w-remove" data-testid="waste-remove-dispose" />
                  <span className="text-sm text-[#2C3E35]">I need Honeystone to remove &amp; dispose of the waste</span>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-base">Visit preference</Label>
              <p className="text-xs text-[#73777B] mt-1">How would you like the visit to work?</p>
              <RadioGroup value={form.visit_preference} onValueChange={(v) => setField("visit_preference", v)} className="mt-3 grid gap-3">
                <label className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer ${form.visit_preference === "meet_greet" ? "border-[#4F6F52] bg-[#F4F8F1]" : "border-[#E5E0D8]"}`}>
                  <RadioGroupItem value="meet_greet" id="vp-meet" data-testid="visit-meet-greet" className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-semibold text-[#2C3E35]">Meet &amp; greet</span>
                    <span className="block text-xs text-[#73777B] mt-0.5">I'd like a chat and a quick walk-around before you start.</span>
                  </span>
                </label>
                <label className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer ${form.visit_preference === "do_not_disturb" ? "border-[#4F6F52] bg-[#F4F8F1]" : "border-[#E5E0D8]"}`}>
                  <RadioGroupItem value="do_not_disturb" id="vp-dnd" data-testid="visit-do-not-disturb" className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-semibold text-[#2C3E35]">Do not disturb</span>
                    <span className="block text-xs text-[#73777B] mt-0.5">Please just crack on with the work and invoice me later — no need to knock.</span>
                  </span>
                </label>
              </RadioGroup>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="step-photos">
            <div>
              <Label className="text-base">Photos of the area</Label>
              <p className="text-xs text-[#73777B] mt-1">Clear shots help us give a more accurate quote.</p>
            </div>
            <PhotoUpload photos={photos} setPhotos={setPhotos} />
            <div>
              <Label htmlFor="notes">Additional notes (optional)</Label>
              <Textarea id="notes" rows={4} value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Access, preferred days, anything else we should know…" data-testid="input-notes" className="mt-1.5" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5" data-testid="step-review">
            <div className="rounded-xl bg-[#F4F8F1] border border-[#D6E2CB] p-5 flex items-start gap-3">
              <Leaf size={20} weight="fill" className="text-[#4F6F52] mt-0.5" />
              <div className="text-sm text-[#2C3E35]">
                Please review your details below. Once submitted, we'll review your photos and send a no-obligation quote.
              </div>
            </div>
            <ReviewBlock label="Name" value={form.name} />
            <ReviewBlock label="Phone" value={form.phone} />
            <ReviewBlock label="Email" value={form.email} />
            <ReviewBlock label="Postcode/Address" value={form.address} />
            <ReviewBlock
              label="Frequency"
              value={form.frequency === "one_off" ? "One-off job" : form.frequency_cadence ? form.frequency_cadence.charAt(0).toUpperCase() + form.frequency_cadence.slice(1) : "—"}
            />
            <ReviewBlock label="Services" value={SERVICES.filter((s) => form.services.includes(s.id)).map((s) => s.label).join(", ") + (form.other_service ? ` — ${form.other_service}` : "")} />
            <ReviewBlock label="Waste disposal" value={form.waste_disposal === "green_bin" ? "Green council bin (leave on site)" : "Honeystone removes & disposes"} />
            <ReviewBlock label="Visit preference" value={form.visit_preference === "meet_greet" ? "Meet & greet — chat & walk-around" : form.visit_preference === "do_not_disturb" ? "Do not disturb — just do the job, invoice later" : "—"} />
            <ReviewBlock label="Notes" value={form.notes || "—"} />
            <ReviewBlock label="Photos attached" value={`${photos.length}`} />
          </div>
        )}

        {/* Nav */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={back} disabled={step === 0} className="rounded-full h-11 px-5" data-testid="back-button">
            <ArrowLeft size={16} weight="bold" className="mr-1.5" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6" data-testid="next-button">
              Next <ArrowRight size={16} weight="bold" className="ml-1.5" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6" data-testid="submit-button">
              {submitting ? "Submitting…" : "Submit request"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#E5E0D8] pb-3 last:border-0">
      <span className="text-xs font-semibold tracking-wide uppercase text-[#73777B]">{label}</span>
      <span className="text-sm text-[#2C3E35] text-right max-w-[70%]">{value || "—"}</span>
    </div>
  );
}
